import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { safeGetItem } from './storageUtils';
import {
  getProviderAdapter,
  getProviderId,
  getPublishableProviderIds,
  normalizeProviderConfig,
} from '../integrations/formConfigAdapters';

export function getFormIntegrationConfigsStorageKey({
  formId,
  viewMode = 'auto',
}) {
  if (!formId) {
    return null;
  }

  return `form_${formId}_integrationConfigs__${viewMode || 'auto'}`;
}

async function syncSingleProviderConfig({
  organizationId,
  formId,
  provider,
  rawConfig,
}) {
  const providerId = getProviderId(provider);
  const adapter = getProviderAdapter(providerId);
  const config = normalizeProviderConfig(providerId, rawConfig);

  if (!providerId || !adapter || !config) {
    return {
      provider: providerId || provider,
      status: 'skipped',
      reason: 'unsupported_provider',
    };
  }

  if (!adapter.saveCallableName) {
    return {
      provider: providerId,
      status: 'skipped',
      reason: 'missing_save_callable',
    };
  }

  const callable = httpsCallable(functions, adapter.saveCallableName);

  const response = await callable({
    organizationId,
    formId,
    provider: providerId,
    config,
  });

  return {
    provider: providerId,
    status: 'success',
    config: response?.data?.config || null,
  };
}

async function publishSingleProviderConfig({
  organizationId,
  formId,
  provider,
}) {
  const providerId = getProviderId(provider);
  const adapter = getProviderAdapter(providerId);

  if (!providerId || !adapter?.publishable) {
    return {
      provider: providerId || provider,
      status: 'skipped',
      reason: 'provider_not_publishable',
    };
  }

  if (!adapter.publishCallableName) {
    return {
      provider: providerId,
      status: 'skipped',
      reason: 'missing_publish_callable',
    };
  }

  const callable = httpsCallable(functions, adapter.publishCallableName);

  const response = await callable({
    organizationId,
    formId,
    provider: providerId,
  });

  return {
    provider: providerId,
    status: 'success',
    config: response?.data?.config || null,
  };
}

function getStagedConfigs({
  formId,
  viewMode,
}) {
  const storageKey = getFormIntegrationConfigsStorageKey({
    formId,
    viewMode,
  });

  const stagedConfigs = storageKey ? safeGetItem(storageKey) : null;

  if (
    !stagedConfigs ||
    typeof stagedConfigs !== 'object' ||
    Array.isArray(stagedConfigs)
  ) {
    return {
      storageKey,
      stagedConfigs: null,
    };
  }

  return {
    storageKey,
    stagedConfigs,
  };
}

function getSyncedPublishableProviderIds({
  stagedConfigs,
  syncResult,
}) {
  const requestedProviderIds = getPublishableProviderIds(stagedConfigs || {});

  if (!requestedProviderIds.length) {
    return [];
  }

  const syncedProviderIds = new Set(
    (syncResult?.synced || [])
      .filter((result) => result?.status === 'success')
      .map((result) => getProviderId(result.provider))
      .filter(Boolean)
  );

  return requestedProviderIds.filter((provider) => {
    const providerId = getProviderId(provider);
    return providerId && syncedProviderIds.has(providerId);
  });
}
export async function syncStagedFormIntegrationConfigs({
  formId,
  organizationId,
  viewMode = 'auto',
}) {
  if (!formId || !organizationId) {
    console.warn('[formIntegrationConfigSync] Missing form/org context:', {
      formId,
      organizationId,
    });

    return {
      status: 'skipped',
      reason: 'missing_context',
      synced: [],
      failed: [],
    };
  }

  const {
    storageKey,
    stagedConfigs,
  } = getStagedConfigs({
    formId,
    viewMode,
  });

  if (!stagedConfigs) {
    console.log('[formIntegrationConfigSync] No staged configs found:', {
      formId,
      organizationId,
      viewMode,
      storageKey,
    });

    return {
      status: 'skipped',
      reason: 'no_staged_configs',
      storageKey,
      synced: [],
      failed: [],
    };
  }

  const entries = Object.entries(stagedConfigs);
  const syncResults = await Promise.allSettled(
    entries.map(([provider, rawConfig]) => {
      return syncSingleProviderConfig({
        organizationId,
        formId,
        provider,
        rawConfig,
      });
    })
  );

  const synced = [];
  const failed = [];

  syncResults.forEach((result, index) => {
    const provider = entries[index]?.[0] || 'unknown';

    if (result.status === 'fulfilled') {
      if (result.value?.status === 'success') {
        synced.push(result.value);
        return;
      }

      console.warn(
        '[formIntegrationConfigSync] Provider sync skipped:',
        result.value
      );

      return;
    }

    console.warn(
      '[formIntegrationConfigSync] Provider sync failed:',
      {
        provider,
        message: result.reason?.message || String(result.reason),
        code: result.reason?.code || null,
      }
    );

    failed.push({
      provider,
      code: result.reason?.code || null,
      message: result.reason?.message || String(result.reason),
    });
  });

  return {
    status: failed.length > 0 ? 'partial' : 'success',
    storageKey,
    synced,
    failed,
  };
}

export async function publishStagedFormIntegrationConfigs({
  formId,
  organizationId,
  viewMode = 'auto',
}) {
  if (!formId || !organizationId) {
    console.warn('[formIntegrationConfigSync] Missing publish context:', {
      formId,
      organizationId,
    });

    return {
      status: 'skipped',
      reason: 'missing_context',
      syncResult: null,
      published: [],
      failed: [],
    };
  }

  const {
    storageKey,
    stagedConfigs,
  } = getStagedConfigs({
    formId,
    viewMode,
  });

  const syncResult = await syncStagedFormIntegrationConfigs({
    formId,
    organizationId,
    viewMode,
  });

  const requestedProviderIds = getPublishableProviderIds(stagedConfigs || {});
  const providerIds = getSyncedPublishableProviderIds({
    stagedConfigs,
    syncResult,
  });

  if (!requestedProviderIds.length) {
    console.log(
      '[formIntegrationConfigSync] No staged publishable providers found:',
      {
        formId,
        organizationId,
        viewMode,
        storageKey,
        syncStatus: syncResult?.status || null,
      }
    );

    return {
      status: 'skipped',
      reason: 'no_staged_publishable_providers',
      storageKey,
      syncResult,
      published: [],
      failed: [],
    };
  }

  if (!providerIds.length) {
    console.warn(
      '[formIntegrationConfigSync] No providers were published because ' +
        'no staged providers synced successfully:',
      {
        formId,
        organizationId,
        viewMode,
        storageKey,
        requestedProviderIds,
        syncStatus: syncResult?.status || null,
        syncFailed: syncResult?.failed || [],
      }
    );

    return {
      status: 'skipped',
      reason: 'no_successfully_synced_publishable_providers',
      storageKey,
      syncResult,
      requestedProviderIds,
      published: [],
      failed: syncResult?.failed || [],
    };
  }

  if ((syncResult?.failed || []).length > 0) {
    console.warn(
      '[formIntegrationConfigSync] Some staged providers failed to sync; ' +
        'publishing only providers that synced successfully:',
      {
        formId,
        organizationId,
        viewMode,
        storageKey,
        requestedProviderIds,
        providerIds,
        failed: syncResult.failed,
      }
    );
  }

  const publishResults = await Promise.allSettled(
    providerIds.map((provider) => {
      return publishSingleProviderConfig({
        organizationId,
        formId,
        provider,
      });
    })
  );

  const published = [];
  const failed = [];

  publishResults.forEach((result, index) => {
    const provider = providerIds[index] || 'unknown';

    if (result.status === 'fulfilled') {
      if (result.value?.status === 'success') {
        published.push(result.value);
        return;
      }

      console.warn(
        '[formIntegrationConfigSync] Provider publish skipped:',
        result.value
      );

      return;
    }

    console.warn(
      '[formIntegrationConfigSync] Provider publish failed:',
      {
        provider,
        message: result.reason?.message || String(result.reason),
        code: result.reason?.code || null,
      }
    );

    failed.push({
      provider,
      code: result.reason?.code || null,
      message: result.reason?.message || String(result.reason),
    });
  });

  return {
    status: failed.length > 0 ? 'partial' : 'success',
    storageKey,
    syncResult,
    published,
    failed,
  };
}
